import { useEffect, useMemo, useState } from "react";
import { api, StockAlert, StockAlertsResponse, StockAlertStatus } from "../api";
import { formatNumber } from "../utils";
import { searchItems } from "../search";
import { GlassPanel, KpiCard, SkeletonTable, SkeletonKpi, ErrorState, DataTable, Badge } from "../components";
import type { Column } from "../components";

type FilterStatus = "all" | StockAlertStatus;

export default function StockAlertsPage() {
  const [data, setData] = useState<StockAlertsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    setError(null);
    api.inventoryAlerts()
      .then((res) => setData(res))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.alerts;
    if (filter !== "all") rows = rows.filter((r) => r.status === filter);
    if (search.trim()) {
      rows = searchItems(rows, search, (r) => ({
        id: r.product_id,
        sku: r.sku,
        name: r.name,
      })).map(({ item }) => item);
    }
    return rows;
  }, [data, filter, search]);

  const statusBadge = (s: StockAlertStatus) => {
    switch (s) {
      case "urgent": return <Badge variant="bad">Khẩn cấp</Badge>;
      case "warning": return <Badge variant="warn">Cảnh báo</Badge>;
      case "slow": return <Badge variant="info">Bán chậm</Badge>;
      case "ok": return <Badge variant="ok">Ổn</Badge>;
    }
  };

  const columns: Column<StockAlert>[] = useMemo(() => [
    {
      key: "sku",
      header: "SKU",
      sortable: true,
      render: (r) => <span className="meta">{r.sku || "—"}</span>,
    },
    {
      key: "name",
      header: "Tên",
      sortable: true,
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          {r.category && <div className="meta" style={{ marginTop: 2 }}>{r.category}</div>}
        </div>
      ),
    },
    {
      key: "current_stock",
      header: "Tồn hiện tại",
      sortable: true,
      align: "right",
      render: (r) => (
        <span style={{ fontWeight: 700, color: r.current_stock <= 0 ? "var(--accent-red)" : "var(--text-primary)" }}>
          {formatNumber(r.current_stock)}
        </span>
      ),
    },
    {
      key: "sold_30d",
      header: "Bán 30d",
      sortable: true,
      align: "right",
      render: (r) => formatNumber(r.sold_30d),
    },
    {
      key: "daily_velocity",
      header: "Velocity/ngày",
      sortable: true,
      align: "right",
      render: (r) => r.daily_velocity > 0 ? r.daily_velocity.toFixed(2) : "—",
    },
    {
      key: "days_remaining",
      header: "Còn bán được",
      sortable: true,
      align: "right",
      render: (r) => {
        if (r.days_remaining === null) return <span className="meta">—</span>;
        const color = r.days_remaining < 3 ? "var(--accent-red)" : r.days_remaining < 7 ? "var(--accent-yellow)" : "var(--text-primary)";
        return <span style={{ fontWeight: 600, color }}>{r.days_remaining.toFixed(1)} ngày</span>;
      },
    },
    {
      key: "status",
      header: "Trạng thái",
      render: (r) => statusBadge(r.status),
    },
    {
      key: "recommended_reorder",
      header: "Gợi ý đặt",
      align: "right",
      render: (r) => r.recommended_reorder > 0
        ? <span style={{ fontWeight: 700, color: "var(--accent-blue)" }}>+{formatNumber(r.recommended_reorder)}</span>
        : <span className="meta">—</span>,
    },
  ], []);

  if (error && !data) {
    return <ErrorState error={error} onRetry={load} />;
  }

  return (
    <>
      {loading && !data ? (
        <div className="kpi-grid">
          <SkeletonKpi /><SkeletonKpi /><SkeletonKpi /><SkeletonKpi />
        </div>
      ) : data && (
        <div className="kpi-grid">
          <KpiCard
            label="Khẩn cấp"
            value={formatNumber(data.summary.urgent)}
            note="< 3 ngày hoặc hết hàng"
            dotColor="bad"
          />
          <KpiCard
            label="Cảnh báo"
            value={formatNumber(data.summary.warning)}
            note="< 7 ngày"
            dotColor="warn"
          />
          <KpiCard
            label="Bán chậm"
            value={formatNumber(data.summary.slow)}
            note="< 3 cái/tháng"
            dotColor=""
          />
          <KpiCard
            label="Ổn"
            value={formatNumber(data.summary.ok)}
            note=">= 7 ngày"
            dotColor="ok"
          />
        </div>
      )}

      <GlassPanel
        title="Cảnh báo tồn kho"
        badge={`${filtered.length} sản phẩm`}
        badgeVariant={filter === "urgent" ? "bad" : filter === "warning" ? "warn" : "info"}
      >
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterStatus)}
            className="input"
            style={{ minWidth: 140 }}
          >
            <option value="all">Tất cả</option>
            <option value="urgent">Khẩn cấp</option>
            <option value="warning">Cảnh báo</option>
            <option value="slow">Bán chậm</option>
            <option value="ok">Ổn</option>
          </select>
          <input
            type="text"
            className="input"
            placeholder="Tìm theo tên / SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 180 }}
          />
        </div>

        {loading && !data ? (
          <SkeletonTable rows={8} cols={8} />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>
            {data && data.alerts.length === 0
              ? "Chưa có dữ liệu tồn kho. Hãy đồng bộ Loyverse trước."
              : "Không tìm thấy sản phẩm phù hợp."}
          </div>
        ) : (
          <div className="table-wrap">
            <DataTable columns={columns} rows={filtered} rowKey={(r) => r.product_id} pageSize={50} />
          </div>
        )}
      </GlassPanel>
    </>
  );
}
